import { useCallback, useRef } from "react";
import styles from "./ClassOptionsGrid.module.scss";

/**
 * Segmented primitive used throughout the class-options grid.
 *
 * Visual spec (round-1 design):
 * - Background `--lilia-bg-muted`, 5px radius, 2px padding
 * - Selected: `--lilia-surface` bg, 600 weight, subtle 1px shadow
 * - Unselected: transparent, muted color, 500 weight
 *
 * Keyboard:
 * - ArrowLeft / ArrowRight cycle between options.
 * - Tab moves focus in/out as a single tab stop (only the selected
 *   option is in the natural tab order).
 */
export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
}

export interface SegmentedProps<T extends string | number> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (next: T) => void;
  /** Optional aria-label for screen readers. */
  ariaLabel?: string;
  /** Optional className passthrough for layout adjustments. */
  className?: string;
  /** On the radiogroup, e.g. `class-option-font-size`; its options are `segmented-option` + data-value. */
  testId?: string;
}

export function Segmented<T extends string | number>(props: SegmentedProps<T>) {
  const { options, value, onChange, ariaLabel, className, testId } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }
      event.preventDefault();
      const currentIndex = options.findIndex((option) => option.value === value);
      if (currentIndex === -1) {
        return;
      }
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (currentIndex + direction + options.length) % options.length;
      const nextOption = options[nextIndex];
      onChange(nextOption.value);
      // Move focus to the newly-selected button so screen readers announce it.
      const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>("button");
      buttons?.[nextIndex]?.focus();
    },
    [options, value, onChange],
  );

  const containerClassName = className ? `${styles.seg} ${className}` : styles.seg;

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      onKeyDown={handleKeyDown}
    >
      {options.map((option) => {
        const isOn = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={isOn}
            data-testid="segmented-option"
            data-value={String(option.value)}
            tabIndex={isOn ? 0 : -1}
            className={isOn ? `${styles.segButton} ${styles.segButtonOn}` : styles.segButton}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
